import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getVendors, updateVendorStatus } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminVendors.css';


const FILTER_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
];

const STATUS_CLS = {
  pending:   'badge-warning',
  approved:  'badge-success',
  rejected:  'badge-danger',
  suspended: 'badge-danger',
};

export default function AdminVendors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [filter, setFilter] = useState(searchParams.get('tab') || 'all');

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getVendors({ limit: 200 });
      setVendors(data.vendors || []);
    } catch (err) {
      console.error('Admin vendors error:', err?.response?.data || err.message);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleStatusChange = async (vendor, newStatus) => {
    setActioning(vendor._id + newStatus);
    try {
      await updateVendorStatus(vendor._id, { status: newStatus });
      setVendors((prev) => prev.map((v) => v._id === vendor._id ? { ...v, status: newStatus } : v));
      toast.success(`Vendor ${newStatus}`);
    } catch {
      toast.error('Failed to update vendor status');
    } finally {
      setActioning(null);
    }
  };

  const changeTab = (key) => {
    setFilter(key);
    setSearchParams(key === 'all' ? {} : { tab: key });
  };

  const filtered = filter === 'all' ? vendors : vendors.filter((v) => v.status === filter);

  return (
    <AdminLayout title="Vendors">
      <div className="avendors-page">
        {/* Filter tabs */}
        <div className="avendors-tabs">
          {FILTER_TABS.map((tab) => {
            const count = tab.key === 'all' ? vendors.length : vendors.filter((v) => v.status === tab.key).length;
            return (
              <button
                key={tab.key}
                className={`avendors-tab ${filter === tab.key ? 'active' : ''} ${tab.key === 'pending' && count > 0 ? 'has-pending' : ''}`}
                onClick={() => changeTab(tab.key)}
              >
                {tab.label}
                {count > 0 && <span className="avendors-tab-count">{count}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /><span>Loading vendors…</span></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏪</div>
            <h3>{filter === 'all' ? 'No vendors yet' : `No ${filter} vendors`}</h3>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Owner</th>
                  <th style={{ textAlign: 'center' }}>Products</th>
                  <th>Total Sales</th>
                  <th>Joined</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((vendor) => {
                  const joined = new Date(vendor.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
                  return (
                    <tr key={vendor._id}>
                      {/* Store */}
                      <td>
                        <div className="av-store-cell">
                          <div className="av-store-logo">
                            {vendor.logo
                              ? <img src={vendor.logo} alt={vendor.storeName} />
                              : <span>{vendor.storeName?.[0]?.toUpperCase()}</span>}
                          </div>
                          <p className="av-store-name">{vendor.storeName}</p>
                        </div>
                      </td>

                      {/* Owner */}
                      <td>
                        <p className="font-medium" style={{ margin: 0 }}>{vendor.user?.name || '—'}</p>
                        <p className="text-muted text-sm" style={{ margin: 0 }}>{vendor.user?.email}</p>
                      </td>

                      {/* Products */}
                      <td style={{ textAlign: 'center', color: 'var(--text-2)', fontWeight: 600 }}>
                        {vendor.totalProducts ?? '—'}
                      </td>

                      {/* Sales */}
                      <td className="font-semi">
                        {vendor.totalSales ? `Rs. ${Number(vendor.totalSales).toLocaleString('en-PK')}` : '—'}
                      </td>

                      {/* Joined */}
                      <td className="text-muted text-sm">{joined}</td>

                      {/* Status */}
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${STATUS_CLS[vendor.status] || 'badge-info'}`}>{vendor.status}</span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="av-actions">
                          {vendor.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-sm av-approve-btn"
                                onClick={() => handleStatusChange(vendor, 'approved')}
                                disabled={!!actioning}
                                title="Approve"
                              >
                                <FiCheck size={13} /> Approve
                              </button>
                              <button
                                className="btn btn-sm av-reject-btn"
                                onClick={() => handleStatusChange(vendor, 'rejected')}
                                disabled={!!actioning}
                                title="Reject"
                              >
                                <FiX size={13} /> Reject
                              </button>
                            </>
                          )}
                          {vendor.status === 'approved' && (
                            <button
                              className="btn btn-sm av-suspend-btn"
                              onClick={() => handleStatusChange(vendor, 'suspended')}
                              disabled={!!actioning}
                              title="Suspend"
                            >
                              <FiX size={13} /> Suspend
                            </button>
                          )}
                          {(vendor.status === 'suspended' || vendor.status === 'rejected') && (
                            <button
                              className="btn btn-sm av-reactivate-btn"
                              onClick={() => handleStatusChange(vendor, 'approved')}
                              disabled={!!actioning}
                              title="Reactivate"
                            >
                              <FiRefreshCw size={13} /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
